import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback } from 'react';
import { gameSocket } from '../lib/socket';
import { battleSocket } from '../lib/battleSocket';
import { StorageHelpers } from '../lib/storage';
import logger from '../lib/logger';
import config from '../config';
import { 
  User, 
  Character, 
  CharacterClass, 
  CharacterStats,
  BattleRequest, 
  BattleResult, 
  Notification,
  Location,
  CharacterTraveling 
} from '../types';

interface GameState {
  // Аутентификация
  isAuthenticated: boolean;
  user: User | null;
  
  // Персонаж
  character: Character | null;
  characterLoading: boolean;
  
  // Соединение
  socketConnected: boolean;
  connectionStatus: string;
  
  // Заявки на бой
  battleRequests: BattleRequest[];
  myActiveRequest: BattleRequest | null;
  battleResult: BattleResult | null;
  
  // Классы персонажей
  characterClasses: CharacterClass[];
  characterClassesLoading: boolean;
  
  // Локации
  locations: Location[];
  locationsLoading: boolean;
  
  // UI состояние
  isLoading: boolean;
  notifications: Notification[];
}

// Типы действий
type GameAction =
  // Аутентификация
  | { type: 'SET_AUTH'; payload: { isAuthenticated: boolean; user: User | null } }
  | { type: 'LOGOUT' }
  
  // Персонаж
  | { type: 'SET_CHARACTER'; payload: Character | null }
  | { type: 'UPDATE_CHARACTER'; payload: Partial<Character> }
  | { type: 'SET_CHARACTER_LOADING'; payload: boolean }
  
  // Соединение
  | { type: 'SET_SOCKET_CONNECTED'; payload: boolean }
  | { type: 'SET_CONNECTION_STATUS'; payload: string }
  
  // Заявки на бой
  | { type: 'SET_BATTLE_REQUESTS'; payload: BattleRequest[] }
  | { type: 'SET_MY_ACTIVE_REQUEST'; payload: BattleRequest | null }
  | { type: 'ADD_BATTLE_REQUEST'; payload: BattleRequest }
  | { type: 'REMOVE_BATTLE_REQUEST'; payload: string }
  | { type: 'SET_BATTLE_RESULT'; payload: BattleResult | null }
  
  // Классы персонажей
  | { type: 'SET_CHARACTER_CLASSES'; payload: CharacterClass[] }
  | { type: 'SET_CHARACTER_CLASSES_LOADING'; payload: boolean }
  
  // Локации
  | { type: 'SET_LOCATIONS'; payload: Location[] }
  | { type: 'SET_LOCATIONS_LOADING'; payload: boolean }
  
  // UI
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'ADD_NOTIFICATION'; payload: Omit<Notification, 'id' | 'timestamp'> }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  
  // Универсальные
  | { type: 'UPDATE_STATE'; payload: { path: string; value: any } }
  | { type: 'BATCH_UPDATE'; payload: { updates: Array<{ path: string; value: any }> } };

// Начальное состояние
const initialState: GameState = {
  // Аутентификация
  isAuthenticated: false,
  user: null,
  
  // Персонаж
  character: null,
  characterLoading: false,
  
  // Соединение
  socketConnected: false,
  connectionStatus: 'Инициализация...',
  
  // Заявки на бой
  battleRequests: [],
  myActiveRequest: null,
  battleResult: null,
  
  // Классы персонажей
  characterClasses: [],
  characterClassesLoading: false,
  
  // Локации
  locations: [],
  locationsLoading: false,
  
  // UI
  isLoading: true,
  notifications: []
};

// Reducer
function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    // Аутентификация
    case 'SET_AUTH':
      return { 
        ...state, 
        isAuthenticated: action.payload.isAuthenticated,
        user: action.payload.user 
      };
    
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        character: null,
        socketConnected: false,
        battleRequests: [],
        myActiveRequest: null,
        battleResult: null
      };
    
    // Персонаж
    case 'SET_CHARACTER':
      // Сохраняем старые значения exp_for_current_level и exp_for_next_level если они не пришли в новых данных
      if (action.payload && state.character) {
        const newCharacter = {
          ...action.payload,
          exp_for_current_level: action.payload.exp_for_current_level ?? state.character.exp_for_current_level,
          exp_for_next_level: action.payload.exp_for_next_level ?? state.character.exp_for_next_level
        };
        return { ...state, character: newCharacter };
      }
      return { ...state, character: action.payload };
    
    case 'UPDATE_CHARACTER':
      if (!state.character) return state;
      return {
        ...state,
        character: { ...state.character, ...action.payload }
      };
    
    case 'SET_CHARACTER_LOADING':
      return { ...state, characterLoading: action.payload };
    
    // Соединение
    case 'SET_SOCKET_CONNECTED':
      return { ...state, socketConnected: action.payload };
    
    case 'SET_CONNECTION_STATUS':
      return { ...state, connectionStatus: action.payload };
    
    // Заявки на бой
    case 'SET_BATTLE_REQUESTS':
      return { ...state, battleRequests: action.payload };
    
    case 'SET_MY_ACTIVE_REQUEST':
      return { ...state, myActiveRequest: action.payload };
    
    case 'ADD_BATTLE_REQUEST':
      if (action.payload.challenger_id === state.character?.id) {
        return state; // Не добавляем свою заявку в общий список
      }
      return {
        ...state,
        battleRequests: [action.payload, ...state.battleRequests]
      };
    
    case 'REMOVE_BATTLE_REQUEST':
      return {
        ...state,
        battleRequests: state.battleRequests.filter(r => r.id !== action.payload),
        myActiveRequest: state.myActiveRequest?.id === action.payload ? null : state.myActiveRequest
      };
    
    case 'SET_BATTLE_RESULT':
      return { ...state, battleResult: action.payload };
    
    // Классы персонажей
    case 'SET_CHARACTER_CLASSES':
      return { ...state, characterClasses: action.payload };
    
    case 'SET_CHARACTER_CLASSES_LOADING':
      return { ...state, characterClassesLoading: action.payload };
    
    // Локации
    case 'SET_LOCATIONS':
      return { ...state, locations: action.payload };
    
    case 'SET_LOCATIONS_LOADING':
      return { ...state, locationsLoading: action.payload };
    
    // UI
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'ADD_NOTIFICATION':
      const notification: Notification = {
        ...action.payload,
        id: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now()
      };
      return {
        ...state,
        notifications: [...state.notifications, notification]
      };
    
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload)
      };
    
    case 'UPDATE_STATE':
      // Обновление вложенных свойств по пути (например, 'character.experience')
      const { path, value } = action.payload;
      const keys = path.split('.');
      const newState = { ...state };
      
      let current: any = newState;
      for (let i = 0; i < keys.length - 1; i++) {
        if (current[keys[i]] === undefined) return state;
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      
      return newState;
    
    case 'BATCH_UPDATE':
      // Применяем несколько обновлений за раз
      let batchState = state;
      for (const update of action.payload.updates) {
        batchState = gameReducer(batchState, {
          type: 'UPDATE_STATE',
          payload: update
        });
      }
      return batchState;
    
    default:
      return state;
  }
}

// Контекст
interface GameContextType {
  state: GameState;
  dispatch: React.Dispatch<GameAction>;
  
  // Методы аутентификации
  login: (token: string) => Promise<void>;
  logout: () => void;
  
  // Методы персонажа
  createCharacter: (data: { nickname: string; class_id: string; avatar?: string }) => void;
  loadCharacterClasses: () => void;
  spendStatPoints: (stats: Partial<CharacterStats>) => void;
  
  // Методы боев
  createBattleRequest: () => void;
  cancelBattleRequest: (requestId: string) => void;
  acceptBattleRequest: (requestId: string) => void;
  closeBattleResult: () => void;
  loadBattleRequests: () => void;
  loadMyActiveRequest: () => void;
  
  // Методы локаций
  loadLocations: () => void;
  
  // UI методы
  showNotification: (type: Notification['type'], message: string) => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

// Provider компонент
interface GameProviderProps {
  children: ReactNode;
}

export function GameProvider({ children }: GameProviderProps) {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  // Подписка на WebSocket события
  useEffect(() => {
    // Регистрируем обработчики сразу, не дожидаясь socketConnected
    // События аутентификации
    const unsubAuthSuccess = gameSocket.on('auth.success', (data: { user: any }) => {
      logger.debug('GameContext', 'auth.success event received');
      dispatch({ type: 'SET_AUTH', payload: { isAuthenticated: true, user: data.user } });
      
      // Устанавливаем sessionToken для battleSocket
      const token = StorageHelpers.getToken();
      if (token) {
        battleSocket.setSessionToken(token);
        logger.debug('GameContext', 'Session token set for battleSocket');
      }
    });
    
    const unsubAuthError = gameSocket.on('auth.error', () => {
      StorageHelpers.clearToken();
      dispatch({ type: 'LOGOUT' });
    });
    
    // События персонажа
    const unsubCharacterData = gameSocket.on('character.data', (data: { character: Character | null }) => {
      console.log('GameContext: Received character.data:', data.character);
      dispatch({ type: 'SET_CHARACTER', payload: data.character });
      dispatch({ type: 'SET_CHARACTER_LOADING', payload: false });
      if (data.character) {
        StorageHelpers.saveCharacter(data.character);
        
        // Проверяем, есть ли активный бой
        if ((data.character as any).activeBattleId) {
          console.log('GameContext: Character has active battle:', (data.character as any).activeBattleId);
          // Эмулируем событие об активном бое
          // Событие будет обработано в GameScreen
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('character-active-battle', {
              detail: {
                battleId: (data.character as any).activeBattleId,
                message: 'У вас есть активный бой. Подключение...'
              }
            }));
          }, 100);
        }
      }
    });
    
    const unsubCharacterCreated = gameSocket.on('character.created', (data: { character: Character }) => {
      // Сохраняем базовые данные созданного персонажа
      dispatch({ type: 'SET_CHARACTER', payload: data.character });
      StorageHelpers.saveCharacter(data.character);
      
      // Устанавливаем флаг загрузки персонажа
      dispatch({ type: 'SET_CHARACTER_LOADING', payload: true });
      
      // Запрашиваем полные данные персонажа (с производными характеристиками)
      logger.debug('GameContext', 'Character created, requesting full data...');
      gameSocket.emit('character.get');
    });
    
    const unsubCharacterUpdate = gameSocket.on('character.update', (data: Partial<Character>) => {
      dispatch({ type: 'UPDATE_CHARACTER', payload: data });
    });
    
    // События заявок на бой
    const unsubBattleRequestList = gameSocket.on('battle-request.list-update', (data: { requests: BattleRequest[] }) => {
      dispatch({ type: 'SET_BATTLE_REQUESTS', payload: data.requests });
    });
    
    const unsubMyActiveRequest = gameSocket.on('battle-request.my-active-result', (data: { request: BattleRequest | null }) => {
      dispatch({ type: 'SET_MY_ACTIVE_REQUEST', payload: data.request });
    });
    
    const unsubRequestCreated = gameSocket.on('battle-request.created', (data: { request: BattleRequest }) => {
      dispatch({ type: 'SET_MY_ACTIVE_REQUEST', payload: data.request });
    });
    
    const unsubRequestCancelled = gameSocket.on('battle-request.cancelled', () => {
      dispatch({ type: 'SET_MY_ACTIVE_REQUEST', payload: null });
      // Обновление списка заявок будет через метод loadBattleRequests
    });
    
    const unsubRequestNew = gameSocket.on('battle-request.new', (data: { request: BattleRequest }) => {
      dispatch({ type: 'ADD_BATTLE_REQUEST', payload: data.request });
    });
    
    const unsubRequestRemoved = gameSocket.on('battle-request.removed', (data: { requestId: string }) => {
      dispatch({ type: 'REMOVE_BATTLE_REQUEST', payload: data.requestId });
    });
    
    const unsubBattleResult = gameSocket.on('battle.auto-result', (data: any) => {
      dispatch({ type: 'SET_BATTLE_RESULT', payload: data });
    });
    
    // События ошибок
    const unsubRequestError = gameSocket.on('battle-request.error', (data: { error: string }) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: data.error } });
    });
    
    const unsubCharacterError = gameSocket.on('character.error', (data: { error: string }) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: data.error } });
    });
    
    // Событие уведомления
    const unsubNotification = gameSocket.on('notification', (data: { type: Notification['type']; message: string }) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: data });
    });
    
    // События классов персонажей
    const unsubCharacterClasses = gameSocket.on('character-classes.data', (data: { classes: CharacterClass[] }) => {
      dispatch({ type: 'SET_CHARACTER_CLASSES', payload: data.classes });
      dispatch({ type: 'SET_CHARACTER_CLASSES_LOADING', payload: false });
    });
    
    // События локаций
    const unsubLocations = gameSocket.on('locations.data', (data: { locations: Location[] }) => {
      console.log('GameContext: Received locations.data:', data);
      console.log('GameContext: Locations count:', data.locations?.length);
      dispatch({ type: 'SET_LOCATIONS', payload: data.locations });
      dispatch({ type: 'SET_LOCATIONS_LOADING', payload: false });
    });
    
    // События перемещения
    const unsubTravelStarted = gameSocket.on('location.travel-started', (data: { travelInfo: CharacterTraveling }) => {
      dispatch({ type: 'UPDATE_CHARACTER', payload: { current_location_id: data.travelInfo } });
    });
    
    const unsubTravelCompleted = gameSocket.on('location.travel-completed', (data: { locationId: string }) => {
      dispatch({ type: 'UPDATE_CHARACTER', payload: { 
        current_location_id: {
          type: 'in_location',
          location_id: data.locationId
        }
      }});
    });
    
    const unsubTravelError = gameSocket.on('location.travel-error', (data: { error: string }) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: data.error } });
    });
    
    // События соединения
    const unsubDisconnect = gameSocket.on('disconnect', () => {
      dispatch({ type: 'SET_SOCKET_CONNECTED', payload: false });
      dispatch({ type: 'ADD_NOTIFICATION', payload: { type: 'error', message: 'Соединение с сервером потеряно' } });
    });

    return () => {
      unsubAuthSuccess();
      unsubAuthError();
      unsubCharacterData();
      unsubCharacterCreated();
      unsubCharacterUpdate();
      unsubBattleRequestList();
      unsubMyActiveRequest();
      unsubRequestCreated();
      unsubRequestCancelled();
      unsubRequestNew();
      unsubRequestRemoved();
      unsubBattleResult();
      unsubRequestError();
      unsubCharacterError();
      unsubNotification();
      unsubCharacterClasses();
      unsubLocations();
      unsubTravelStarted();
      unsubTravelCompleted();
      unsubTravelError();
      unsubDisconnect();
    };
  }, []); // Убираем зависимость от state.socketConnected

  // Автоматическое удаление уведомлений через 5 секунд
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    
    state.notifications.forEach(notification => {
      const timer = setTimeout(() => {
        dispatch({ type: 'REMOVE_NOTIFICATION', payload: notification.id });
      }, 5000);
      timers.push(timer);
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [state.notifications]);

  // Проверка авторизации через cookie
  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${config.api.baseUrl}/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.user;
      }
      return null;
    } catch (error) {
      logger.error('GameContext', 'Check auth error:', error);
      return null;
    }
  }, []);

  // Методы аутентификации
  const login = useCallback(async (token?: string) => {
    logger.debug('GameContext', 'Login method called');
    try {
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'Подключение к серверу...' });
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Если токен не передан, пытаемся проверить авторизацию через cookie
      if (!token) {
        const user = await checkAuth();
        if (!user) {
          throw new Error('Not authenticated');
        }
        
        // Создаем временный токен для WebSocket соединения
        token = 'cookie-auth';
      }
      
      logger.debug('GameContext', 'Connecting to socket...');
      await gameSocket.connect(token);
      logger.debug('GameContext', 'Socket connected');
      dispatch({ type: 'SET_SOCKET_CONNECTED', payload: true });
      
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'Загрузка персонажа...' });
      dispatch({ type: 'SET_CHARACTER_LOADING', payload: true });
      
      // Не вызываем getCharacter() - сервер автоматически отправит персонажа
      // через событие character.data после успешной авторизации
      logger.debug('GameContext', 'Requesting character data...');
      gameSocket.emit('character.get');
      
      // Ждем немного, чтобы события успели обработаться
      await new Promise(resolve => setTimeout(resolve, 100));
      
      logger.debug('GameContext', 'Login complete, setting loading to false');
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      logger.error('GameContext', 'Login error:', error);
      StorageHelpers.clearToken();
      dispatch({ type: 'LOGOUT' });
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'ADD_NOTIFICATION', payload: { 
        type: 'error', 
        message: 'Ошибка подключения к серверу' 
      }});
      throw error; // Пробрасываем ошибку дальше
    }
  }, [checkAuth]);
  
  // Инициализация при загрузке
  useEffect(() => {
    const initializeApp = async () => {
      logger.debug('GameContext', 'Initializing app...');
      logger.debug('GameContext', 'Current pathname:', window.location.pathname);
      
      // Не инициализируем автоматически, если мы на странице callback
      const isCallbackPage = window.location.pathname === '/auth/callback';
      if (isCallbackPage) {
        logger.debug('GameContext', 'On callback page, skipping auto-init');
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }
      
      // Сначала проверяем cookie-based авторизацию
      try {
        const user = await checkAuth();
        if (user) {
          logger.debug('GameContext', 'Cookie auth successful, logging in');
          await login(); // Вызываем login без токена
          return;
        }
      } catch (error) {
        logger.debug('GameContext', 'Cookie auth failed:', error);
      }
      
      // Если cookie auth не сработал, проверяем токен в localStorage
      const token = StorageHelpers.getToken();
      logger.debug('GameContext', 'Token from storage:', token ? 'present' : 'missing');
      
      if (token) {
        logger.debug('GameContext', 'Auto-login with stored token');
        await login(token);
      } else {
        logger.debug('GameContext', 'No auth, setting loading to false');
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };
    
    initializeApp();
  }, [login, checkAuth]);
  
  const logout = useCallback(() => {
    gameSocket.logout();
    StorageHelpers.clearToken();
    StorageHelpers.clearCharacter();
    dispatch({ type: 'LOGOUT' });
  }, []);
  
  // Методы персонажа
  const createCharacter = useCallback((data: { nickname: string; class_id: string; avatar?: string }) => {
    gameSocket.emit('character.create', data);
  }, []);
  
  const loadCharacterClasses = useCallback(() => {
    if (state.socketConnected) {
      dispatch({ type: 'SET_CHARACTER_CLASSES_LOADING', payload: true });
      gameSocket.emit('character-classes.get');
    }
  }, [state.socketConnected]);
  
  // Методы боев
  const createBattleRequest = useCallback(() => {
    gameSocket.emit('battle-request.create');
  }, []);
  
  const cancelBattleRequest = useCallback((requestId: string) => {
    gameSocket.emit('battle-request.cancel', { requestId });
  }, []);
  
  const acceptBattleRequest = useCallback((requestId: string) => {
    gameSocket.emit('battle-request.accept', { requestId });
  }, []);
  
  const closeBattleResult = useCallback(() => {
    dispatch({ type: 'SET_BATTLE_RESULT', payload: null });
  }, []);
  
  const loadBattleRequests = useCallback(() => {
    if (state.socketConnected) {
      gameSocket.emit('battle-request.list');
    }
  }, [state.socketConnected]);
  
  const loadMyActiveRequest = useCallback(() => {
    if (state.socketConnected) {
      gameSocket.emit('battle-request.my-active');
    }
  }, [state.socketConnected]);

  const spendStatPoints = useCallback((stats: Partial<CharacterStats>) => {
    gameSocket.emit('character.spend_stat_points', { stats });
  }, []);
  
  // Методы локаций
  const loadLocations = useCallback(() => {
    if (state.socketConnected) {
      dispatch({ type: 'SET_LOCATIONS_LOADING', payload: true });
      gameSocket.emit('locations.get');
    }
  }, [state.socketConnected]);
  
  // UI методы
  const showNotification = useCallback((type: Notification['type'], message: string) => {
    dispatch({ type: 'ADD_NOTIFICATION', payload: { type, message } });
  }, []);

  const value: GameContextType = {
    state,
    dispatch,
    login,
    logout,
    createCharacter,
    loadCharacterClasses,
    createBattleRequest,
    cancelBattleRequest,
    acceptBattleRequest,
    closeBattleResult,
    loadBattleRequests,
    loadMyActiveRequest,
    loadLocations,
    showNotification,
    spendStatPoints
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
}

// Hook для использования контекста
export function useGame() {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}

// Экспорт типов
export type { Character, GameState, Notification, BattleRequest, CharacterClass };
